import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight
} from '@fortawesome/free-solid-svg-icons';
import { type Appointment } from '../../services/clinicsService';
import { getStatusColorClasses, getStatusLabel } from '../../utils/appointmentStatus';
import BtnTaratic from '../../components/ui/BtnTaratic';

interface ClinicsAppointmentsListProps {
  appointments: Appointment[];
  rooms: any[];
  onEdit: (appointment: Appointment) => void;
  onDelete: (appointment: Appointment) => void;
  onViewPatient: (patientId: number) => void;
  page?: number;
  totalPages?: number;
  totalAppointments?: number;
  perPage?: number;
  onPageChange?: (newPage: number) => void;
  loading?: boolean;
}

const ClinicsAppointmentsList: React.FC<ClinicsAppointmentsListProps> = ({
  appointments,
  rooms,
  onEdit,
  onDelete,
  onViewPatient,
  page = 1,
  totalPages = 1,
  totalAppointments,
  perPage = 20,
  onPageChange,
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const [inputPage, setInputPage] = useState(String(page));

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleJumpPage = (e: React.FormEvent) => {
    e.preventDefault();
    const p = parseInt(inputPage, 10);
    if (!isNaN(p) && totalPages) {
      const validPage = Math.min(Math.max(1, p), totalPages);
      onPageChange?.(validPage);
      setInputPage(String(validPage));
    } else {
      setInputPage(String(page));
    }
  };

  const getPageNumbers = () => {
    if (!totalPages) return [];
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      if (page <= 3) {
        end = 4;
      }
      if (page >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  const total = totalAppointments !== undefined ? totalAppointments : appointments.length;
  const startItem = total === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);

  return (
    <div data-ui="clinics-appointments-list-region-001" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md flex flex-col h-full min-h-0 overflow-hidden animate-fade-in">
      <div data-ui="clinics-appointments-list-region-002" className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-xs sm:text-sm text-left">
          <thead className="text-[11px] sm:text-xs text-white bg-black/85 backdrop-blur-md border-b border-white/10 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2.5 font-bold">{t('clinics.list.dateTime')}</th>
              <th className="px-4 py-2.5 font-bold">{t('clinics.list.patient')}</th>
              <th className="px-4 py-2.5 font-bold">{t('clinics.list.room')}</th>
              <th className="px-4 py-2.5 font-bold">{t('clinics.list.status')}</th>
              <th className="px-4 py-2.5 font-bold text-right">{t('clinics.list.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {appointments.map((app) => {
              const room = rooms.find((r) => String(r.id) === String(app.room_id));
              const date = new Date(app.start_time);
              return (
                <tr key={app.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 text-white whitespace-nowrap">
                    {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                    {app.patient_name || 'Desconocido'}
                  </td>
                  <td className="px-4 py-3 text-white/80 whitespace-nowrap">
                    {room ? (
                      <div data-ui="clinics-appointments-list-region-003" className="flex items-center gap-2">
                        <div data-ui="clinics-appointments-list-region-004"
                          className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm"
                          style={{ backgroundColor: room.color_code || '#808080' }}
                        />
                        <span className={!room.is_active ? 'opacity-50 line-through' : ''}>
                          {room.name}{' '}
                          {!room.is_active && (
                            <span className="text-[10px] ml-1">
                              {i18n.language === 'en' ? '(inactive)' : '(inactiva)'}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : (
                      app.room_id
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      data-ui="clinics-appointment-status-tag"
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColorClasses(
                        app.status || 'scheduled'
                      )}`}
                    >
                      {getStatusLabel(app.status || 'scheduled')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div data-ui="clinics-appointments-list-region-005" className="flex justify-end gap-2">
                      <BtnTaratic
                        onClick={() => onViewPatient(app.patient_id)}
                        color="ghost-dark"
                        size="xs"
                        title={t('clinics.list.viewPatient')}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </BtnTaratic>
                      <BtnTaratic
                        onClick={() => onEdit(app)}
                        color="ghost-dark"
                        size="xs"
                        title={t('clinics.list.edit')}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </BtnTaratic>
                      <BtnTaratic
                        onClick={() => onDelete(app)}
                        color="red"
                        size="xs"
                        title={t('clinics.list.delete')}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </BtnTaratic>
                    </div>
                  </td>
                </tr>
              );
            })}
            {appointments.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white/50">
                  {t('clinics.list.noAppointments')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {onPageChange && (
        <div data-ui="clinics-appointments-list-region-006" className="px-4 py-2.5 border-t border-white/20 bg-black/60 flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
          <div data-ui="clinics-appointments-list-region-007" className="flex items-center gap-4 text-xs text-white/70 justify-center md:justify-start md:flex-1 w-full md:w-auto">
            <span>
              Mostrando <strong className="text-white font-medium">{startItem}</strong> a{' '}
              <strong className="text-white font-medium">{endItem}</strong> de{' '}
              <strong className="text-white font-medium">{total}</strong> citas
            </span>
            <span className="hidden sm:inline-block text-white/40">|</span>
            <span className="hidden sm:inline-block text-white/60">
              Página <strong className="text-white font-medium">{page}</strong> de{' '}
              <strong className="text-white font-medium">{totalPages}</strong>
            </span>
          </div>

          <div data-ui="clinics-appointments-list-region-008" className="flex items-center justify-center gap-3 flex-wrap sm:flex-nowrap md:flex-initial w-full md:w-auto">
            {/* Direct Jump Input */}
            <form onSubmit={handleJumpPage} className="flex items-center justify-center gap-1.5 text-xs text-white/70">
              <span className="whitespace-nowrap">Ir a pág:</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={inputPage}
                onChange={(e) => setInputPage(e.target.value)}
                onBlur={() => {
                  const p = parseInt(inputPage, 10);
                  if (isNaN(p) || p < 1 || p > totalPages) {
                    setInputPage(String(page));
                  }
                }}
                className="w-12 sm:w-14 bg-black/20 border border-white/20 rounded-lg px-2 py-1 text-center text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <BtnTaratic type="submit" color="ghost-dark" size="xs" className="px-2 py-1">
                Ir
              </BtnTaratic>
            </form>

            {/* Navigation Buttons */}
            <div data-ui="clinics-appointments-list-region-009" className="flex items-center justify-center gap-1">
              <BtnTaratic
                disabled={page === 1 || loading}
                onClick={() => onPageChange(1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Primera página"
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </BtnTaratic>
              <BtnTaratic
                disabled={page === 1 || loading}
                onClick={() => onPageChange(page - 1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Página anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </BtnTaratic>

              <div data-ui="clinics-appointments-list-region-010" className="hidden md:flex items-center gap-1">
                {getPageNumbers().map((pNum, idx) =>
                  typeof pNum === 'number' ? (
                    <BtnTaratic
                      key={idx}
                      onClick={() => onPageChange(pNum)}
                      color={page === pNum ? 'blue' : 'ghost-dark'}
                      size="xs"
                      className={`min-w-[28px] px-2 py-1 font-medium ${page === pNum ? 'shadow-sm' : ''}`}
                    >
                      {pNum}
                    </BtnTaratic>
                  ) : (
                    <span key={idx} className="px-1 text-white/40 text-xs select-none">
                      {pNum}
                    </span>
                  )
                )}
              </div>

              <BtnTaratic
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(page + 1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Página siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </BtnTaratic>
              <BtnTaratic
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(totalPages)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Última página"
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </BtnTaratic>
            </div>
          </div>

          <div data-ui="clinics-appointments-list-region-011" className="hidden md:flex md:flex-1 justify-end"></div>
        </div>
      )}
    </div>
  );
};

export default ClinicsAppointmentsList;
